import { Link } from 'react-router-dom';
import { Cpu, Sparkles, Zap, X } from 'lucide-react';
import { Modal } from '../common/Modal';
import { useLanguage } from '../../features/language/useLanguage';
import type { SubscriptionTier } from '../../services/subscriptionService';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  /** Current tier of the company. Plus tier never opens this modal. */
  tier: SubscriptionTier;
  /** Active types limit for the current tier. */
  limit: number;
  /** Currently active types count. */
  used: number;
  /** Tier the user should upgrade to. */
  requiredTier: SubscriptionTier;
}

const NEXT_ICON: Record<SubscriptionTier, typeof Zap> = {
  free: Zap,
  basic: Sparkles,
  plus: Sparkles,
};

const interpolate = (template: string, vars: Record<string, string | number>) =>
  Object.entries(vars).reduce(
    (acc, [k, v]) => acc.replaceAll(`{${k}}`, String(v)),
    template
  );

export function MachineTypeLimitExceededModal({
  isOpen,
  onClose,
  tier,
  limit,
  used,
  requiredTier,
}: Props) {
  const { t } = useLanguage();
  const NextIcon = NEXT_ICON[requiredTier] ?? Sparkles;

  const bodyKey =
    tier === 'free'
      ? 'subscription.machineTypeLimitModal.bodyFree'
      : 'subscription.machineTypeLimitModal.bodyBasic';
  const bodyText = interpolate(t(bodyKey), { used, limit });

  const upgradeCtaKey =
    requiredTier === 'plus'
      ? 'subscription.machineTypeLimitModal.cta.upgradeToPlus'
      : 'subscription.machineTypeLimitModal.cta.upgradeToBasic';

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('subscription.machineTypeLimitModal.title')}
      maxWidth="lg"
    >
      <div className="space-y-5" data-test-id="machine-type-limit-modal">
        <div className="rounded-xl bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 p-5 flex items-start gap-4">
          <div className="rounded-full bg-amber-100 p-3">
            <Cpu className="h-6 w-6 text-amber-700" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wider text-amber-700">
              {t('subscription.machineTypeLimitModal.eyebrow')}
            </p>
            <p className="mt-1 text-sm text-amber-900">{bodyText}</p>
          </div>
        </div>

        <div className="rounded-lg border border-gray-200 p-4">
          <div className="flex items-baseline justify-between text-sm text-gray-700">
            <span>
              {interpolate(t('subscription.machineTypeQuota.usedOf'), {
                used,
                limit,
              })}
            </span>
            <span className="text-xs text-gray-500">
              {t('subscription.machineTypeLimitModal.reactivateNote')}
            </span>
          </div>
          <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-amber-100">
            <div
              className="h-full bg-gradient-to-r from-amber-500 to-red-500"
              style={{ width: '100%' }}
            />
          </div>
        </div>

        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center justify-center gap-1 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            data-test-id="machine-type-limit-modal-dismiss"
          >
            <X className="h-4 w-4" />
            {t('subscription.machineTypeLimitModal.cta.dismiss')}
          </button>
          <Link
            to="/settings/subscription"
            onClick={onClose}
            className="inline-flex items-center justify-center gap-1 rounded-md bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700"
            data-test-id="machine-type-limit-modal-upgrade"
          >
            <NextIcon className="h-4 w-4" />
            {t(upgradeCtaKey)}
          </Link>
        </div>
      </div>
    </Modal>
  );
}
